import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { NavArrowRight } from 'iconoir-react'
import { ComponentProps, ReactElement, ReactNode, useState } from 'react'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import type { ToggleRelease } from 'app/types/feature-toggle-release'
import { FeatureToggleTable } from './FeatureToggleTable'
import { getToggleMetadata } from './featureToggleMetadata'
import { ARCHIVED_CATEGORY, type FeatureRow } from './featureToggleModel'
import { FEATURE_TOGGLE_RELEASES } from './featureToggleReleases'
import { useToggleTableState } from './useToggleTableState'

type TableProps = ComponentProps<typeof FeatureToggleTable>

type Props = Omit<TableProps, `rows` | `rowState` | `rowNote` | `isRowHighlighted`> & {
  groups: Array<{ category: string; rows: FeatureRow[] }>
}

function releaseLabelFor(row: FeatureRow): ReactNode {
  const slug = getToggleMetadata(row.name)?.release
  const release = slug ? (FEATURE_TOGGLE_RELEASES as Readonly<Record<string, ToggleRelease>>)[slug] : undefined
  return release ? <Badge label={release.name} className="mt-1" /> : null
}

export const ByFeatureView = observer(function ByFeatureView({ groups, ...tableProps }: Props): ReactElement {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set([ARCHIVED_CATEGORY]))
  const rowState = useToggleTableState()

  const toggleGroup = (category: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        const open = !collapsedGroups.has(group.category)
        return (
          <section key={group.category}>
            {/* Group header sits ABOVE the card (Tasks-style) so each category
                reads as a distinct block. */}
            <Button
              variant="ghost"
              size="inline"
              aria-expanded={open}
              onClick={() => toggleGroup(group.category)}
              className="mb-2"
            >
              <NavArrowRight
                className={classNames(`h-3.5 w-3.5 text-subtle transition-transform`, open && `rotate-90`)}
              />
              <span className="text-body-sm font-semibold tracking-tight text-strong">{group.category}</span>
              <span className="text-label-xs font-medium tabular-nums text-minimal">{group.rows.length}</span>
            </Button>

            {open && (
              <FeatureToggleTable rows={group.rows} rowState={rowState} rowNote={releaseLabelFor} {...tableProps} />
            )}
          </section>
        )
      })}
    </div>
  )
})
